'use client';

import {
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from 'utils/cn';

export type Option = {
  value: string;
  label: string;
};

interface Props {
  title?: string;
  titleClassName?: string;
  options: Option[];
  placeholder?: string;
  value?: Option[];
  onChange: (value: any) => void;
  disabled?: boolean;
  isMulti?: boolean;
  isBlueBg?: boolean;
}

const Select = ({
  title,
  titleClassName = '',
  options,
  placeholder = 'Select...',
  value = [],
  onChange,
  disabled = false,
  isMulti = false,
  isBlueBg = false,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const triggerClassName = cn(
    'flex items-center gap w-full px-2.5 py-2.5 border border-[#CBCBCB] rounded-xl bg-inherit hover:cursor-pointer h-[48px] transition-all',
    { 'border-[#007AC1] focus:outline-none': open },
    { 'opacity-50 cursor-not-allowed': disabled },
    { 'bg-primitiveBlue': isBlueBg },
  );

  const getOptions = () => {
    if (!searchValue.trim()) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  };

  const toggleOption = (
    e: ReactMouseEvent<HTMLButtonElement | HTMLDivElement>,
    option: Option,
  ) => {
    e.stopPropagation();

    if (!isMulti) {
      onChange(option);
      setOpen(false);
    } else {
      const currentValues = Array.isArray(value) ? value : [];

      const exists = currentValues.some((item) => item.value === option.value);
      const newValue = exists
        ? currentValues.filter((item) => item.value !== option.value)
        : [...currentValues, option];

      onChange(newValue);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener('click', handleClickOutside);

    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const renderSingleValue = () => {
    const selected = value?.[0]?.label || '';

    return (
      <div className={cn('flex-auto text-left px-1 py-1.5 text-base text-black truncate select-none', disabled && 'cursor-not-allowed opacity-50')}>
        {selected || placeholder}
      </div>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      {title && (
        <div
          className={cn(
            'absolute bottom-9 text-sm text-secondaryText bg-grey z-10 left-2',
            { 'opacity-50': disabled },
            { 'bg-blue-gradient' : isBlueBg },
            titleClassName,
          )}
        >
          {title}
        </div>
      )}

      <div
        className={triggerClassName}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        {!isMulti ? (
          renderSingleValue()
        ) : !value?.length ? (
          <p className="flex-auto px-1 py-1.5 text-[#999999] text-base font-normal select-none">
            {placeholder}
          </p>
        ) : (
          <div className="w-full flex-auto flex flex-wrap gap-1">
            {(value as Option[]).map((item) => (
              <div
                key={item.value}
                className="flex items-center gap-1 px-1.5 bg-[#007AC1] rounded-[8px] text-white select-none"
              >
                {item.label}
                <button
                  className="text-current p-1"
                  onClick={(e) => toggleOption(e, item)}
                >
                  <X size={12} strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="p-1 hover:bg-gray-100 text-gray-500 rounded-md hover:text-black">
          <ChevronDown className={`transition-all ${open && 'rotate-180'}`} size={16} strokeWidth={2.5} />
        </div>
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-[56px] bg-white z-50 rounded-xl border border-primitiveGrey max-h-[320px] py-2 px-1 overflow-y-auto">
          <input
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={t('search')}
            className="w-full mb-2 px-3 py-2 border rounded-lg text-sm outline-none placeholder:select-none"
          />
          <div className='divide-y'>
            {getOptions().length > 0 ? (
              getOptions().map((option, index) => (
                <div
                  key={option.value}
                  className={cn('select-none hover:border-transparent', hoveredIndex && ((hoveredIndex + 1) === index) &&'border-transparent')}
                >
                  <div
                    className="hover:cursor-pointer px-3 py-2 rounded-lg hover:bg-gray-100 z-20"
                    onClick={(e) => toggleOption(e, option)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {option.label}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-3 rounded-lg flex items-center justify-center text-base font-medium bg-gray-100 text-gray-500">
                {t('noOptions')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Select;
